import type { Agreement, FreeReading, OfficerCard, OfficerIntent, OfficerReading as Officer } from "../contract"
import { AttachButton, attachLabel, type Hydrate } from "./Hydrate"
import { lakh } from "../answer/format"
import { OfficerCta } from "../pro/ProButton"
import { Code } from "./Code"

type Allegation = FreeReading["allegations"][number]

const inr = new Intl.NumberFormat("en-IN")
const rupees = (n: number) => `₹${inr.format(Math.round(n))}`

/** The three things only the officer engine can say about an allegation (NA-Card). */
const ENGINE_ROWS = ["Likely call", "Their strongest point", "Your best answer"] as const

/** What the reading tag means: a model's judgement over three runs, checked by code, not a figure. */
const READING = "How settled the officer's reading is: firm when every way of reading the notice lands on the same call, arguable when one view leads and the other is still open, open when it could go either way. A reading, never a prediction of the outcome."
/** What the worked-out tag means, as on every figure of the free reading. */
const WORKED = "Worked out from the Act and the dates and amounts on the notice, the same way every time. Not written by AI."

function Worked() {
  return <span className="nof-agree" title={WORKED}>worked out</span>
}

function TheySay({ a }: { a: Allegation }) {
  if (!a.they_say) return null
  return (
    <div className="nof-row">
      <span className="k">They say</span>
      <blockquote className="nof-quote">“{a.they_say}”</blockquote>
    </div>
  )
}

/** The records this kind of charge turns on: worked out from its words, not a call on this record. */
function TurnsOn({ a }: { a: Allegation }) {
  if (!a.evidence?.documents.length) return null
  return (
    <div className="nof-row">
      <span className="k">What it turns on</span>
      <div>
        <ul className="nof-docs">
          {a.evidence.documents.map((d) => <li key={d.what}><b>{d.what}</b><span>{d.shows}</span></li>)}
        </ul>
        <p className="nof-src">{a.evidence.label}: what an officer weighs on this kind of charge, from the allegation's own words.</p>
      </div>
    </div>
  )
}

export function PaidEdge({ a, onRead, busy = false }: { a: Allegation; caseTitle?: string; onRead: () => void; busy?: boolean }) {
  return (
    <div className="nof-edge">
      <article className="nof-card" aria-label={`${a.id}, the officer's reading`}>
        <header className="nof-hd"><b><span className="mono">{a.id}</span> · {a.title}</b><span className="mono">{rupees(a.amount)}</span></header>
        <TheySay a={a} />
        <TurnsOn a={a} />
        <p className="nof-lock-note" role="status">{busy ? "Starting the officer's reading…" : "Not run yet. These fill in when you start the officer's reading."}</p>
        {ENGINE_ROWS.map((k) => (
          <div className={`nof-row locked${busy ? " running" : ""}`} key={k}>
            <span className="k">{k}</span>
            <span className="nof-lock" aria-label={`${k}: not run yet`}><span /><span /></span>
          </div>
        ))}
      </article>
      <div className="nof-cta">
        <h3>Read it the way the officer will.</h3>
        <p>
          Three runs on each allegation: the likely call, the department's strongest point, your best answer, and which of
          the records above would change it. Then the re-run with your reply, and the order as the officer would write it.
        </p>
        <OfficerCta onRead={onRead} busy={busy} />
      </div>
    </div>
  )
}

/** Where the engine's output sits before it has run, or while it runs: said plainly. */
function EngineSlot({ what, officer }: { what: string; officer?: Officer | null }) {
  const state = officer?.status === "running" ? "being read now" : officer?.status === "failed" ? "the reading did not finish" : "not run yet"
  return <span className={`nof-slot${officer?.status === "running" ? " running" : ""}`}>{what}: {state}</span>
}

export type Confidence = "firm" | "arguable" | "open" | "provisional"

/**
 * How settled a call is, from how the independent readings agree. The reader is told the strength
 * in words, never how many times the notice was read or how the count fell.
 */
export function confidenceOf(agreement?: Agreement, consistent?: boolean): Confidence {
  if (!agreement || agreement.of <= 1) return "provisional"
  if (consistent || agreement.n === agreement.of) return "firm"
  return agreement.n * 2 > agreement.of ? "arguable" : "open"
}

const CONFIDENCE_WORDS: Record<Confidence, string> = {
  firm: "a firm reading",
  arguable: "arguable · the other view is open",
  open: "open · could go either way",
  provisional: "a provisional reading",
}

/** "a firm reading", "arguable · the other view is open", "open · could go either way", or "worked out". */
export function AgreementMark({ agreement, consistent, kind = "reading" }: { agreement?: Agreement; consistent?: boolean; kind?: "code" | "reading" }) {
  if (kind === "code" || !agreement) return <Worked />
  const level = confidenceOf(agreement, consistent)
  return <span className={`nof-agree${level === "arguable" || level === "open" ? " split" : ""}`} title={READING}>{CONFIDENCE_WORDS[level]}</span>
}

/**
 * Readings saved before the counts came out ("2 of 3 read it confirmed: …", "as the runs split",
 * "(2 of 3)") read the way new ones do: the views and how settled they are, never the counts.
 */
export const withoutCounts = (text: string) =>
  text
    .replace(/\b\d+ of \d+ (?:reads|read) it ([a-z]+(?: [a-z]+)?): /g, "read as $1: ")
    .replace(/^read as/, "Read as")
    .replace(/, as the runs split/g, ", as the reading is not settled")
    .replace(/ \(\d+ of \d+\)/g, "")
    .replace(/Low counts what every run confirms; high, what any run confirms/g, "Low counts what is confirmed however the notice is read; high, what any reasonable reading confirms")
    .replace(/in most runs/g, "on the stronger reading")

const CALL_HEAD = /^(Likely (partly )?confirmed|Likely dropped|Could go either way|Falls with the year|Counted with \S+)/

function Call({ card }: { card: OfficerCard }) {
  const call = card.call
  if (!call) return <span className="nof-slot">{card.missing ?? "No run read this allegation."}</span>
  // The call itself carries the colour, with its word; the rest of the line is plain (NA-Card).
  const head = CALL_HEAD.exec(call.word)?.[0] ?? call.word
  return (
    <div className="nof-call">
      <p>
        <span className={`call ${call.split ? "partly" : call.status}`}>{head}</span>
        {call.word.slice(head.length)} <AgreementMark agreement={call.agreement} consistent={call.consistent} kind={card.kind} />
      </p>
      {call.line ? <p className="nof-why">{call.line}</p> : null}
      {call.why_split ? <p className="nof-why">Why it is not settled: {withoutCounts(call.why_split)}</p> : call.why && card.kind === "reading" ? <p className="nof-why">{call.why}</p> : null}
    </div>
  )
}

/** The officer's reading, if not run yet: one line and the button (dev preview), or where it stands. */
export function OfficerStrip({ officer, onRun, busy, dev = false, error = "" }: {
  officer?: Officer | null; onRun: () => void; busy: boolean; dev?: boolean; error?: string
}) {
  if (officer?.status === "done") {
    const m = officer.meta
    return (
      <p className="nrd-note nof-strip">
        Read the way the officer will if no reply is filed. Each call says how settled it is.
        {m && dev ? <span className="mono"> Dev preview: {Math.round(m.seconds)} s, ${m.usd.toFixed(3)}, {inr.format(m.input_tokens)} in / {inr.format(m.output_tokens)} out.</span> : null}
      </p>
    )
  }
  if (officer?.status === "running" || busy) {
    return (
      <p className="nrd-note nof-strip" role="status">
        Reading it the way the officer will. It takes a minute or two; the reading stays here if you leave.
      </p>
    )
  }
  return (
    <div className="nof-strip">
      <p className="nrd-note">
        {error ? `${error} ` : officer?.status === "failed" ? `${officer.error || "The officer's reading did not finish."} ` : "The officer's reading has not been run on this notice. "}
        Three independent runs, a minute or two.
      </p>
      <button type="button" className="ncf-btn primary" onClick={onRun}>{officer?.status === "failed" ? "Try again" : "Run the officer's reading"}</button>
    </div>
  )
}

/** An allegation's periods, only when they differ from the notice's (stated once, in the glance). */
export function ownPeriods(a: { periods: string[]; years?: string[] }, noticeYears: string[]): string {
  const mine = [...(a.years ?? [])].sort().join()
  return mine && mine === [...noticeYears].sort().join() ? "" : a.periods.join(", ")
}

/** Section 02 as a paid reader sees it (NA-Analysis, NA-Card): every allegation as a card. */
export function OfficerCards({ allegations, officer, noticeYears = [], hydrate }: { allegations: Allegation[]; officer?: Officer | null; noticeYears?: string[]; hydrate?: Hydrate }) {
  const cards = new Map((officer?.status === "done" ? officer.allegations ?? [] : []).map((c) => [c.id, c]))
  return (
    <div className="nof-cards">
      {allegations.map((a) => {
        const c = cards.get(a.id)
        return (
          <article className="nof-card open" key={a.id} aria-label={`${a.id}, the officer's reading`}>
            <header className="nof-hd">
              <b><span className="mono">{a.id}</span> · {a.title}</b>
              <span className="mono">{rupees(a.amount)}</span>
            </header>
            <p className="nof-meta">{[ownPeriods(a, noticeYears), a.note].filter(Boolean).join(" · ")}</p>
            <TheySay a={a} />
            {c ? (
              <>
                <div className="nof-row"><span className="k">Likely call</span><Call card={c} /></div>
                {c.strongest_point ? <div className="nof-row"><span className="k">Their strongest point</span><span>{c.strongest_point}</span></div> : null}
                {c.best_answer ? <div className="nof-row"><span className="k">Your best answer</span><span>{c.best_answer}</span></div> : null}
                {c.would_change?.length ? (
                  <div className="nof-row">
                    <span className="k">What would change it</span>
                    <ul className="nof-docs">{c.would_change.map((w) => <li key={w}>{hydrate ? <AttachButton label={attachLabel(w)} what={w} hydrate={hydrate} /> : w}</li>)}</ul>
                  </div>
                ) : null}
              </>
            ) : (
              ENGINE_ROWS.map((k) => <div className="nof-row" key={k}><span className="k">{k}</span><EngineSlot what={k} officer={officer} /></div>)
            )}
            <TurnsOn a={a} />
            {c?.if_intent_fails ? (
              <div className="nof-row"><span className="k">If intent fails</span><span>{c.if_intent_fails.line} <Worked /></span></div>
            ) : null}
            {a.paired_with ? (
              <div className="nof-row"><span className="k">Paired with</span><span>{a.paired_with}: the same money, counted once. <Worked /></span></div>
            ) : null}
          </article>
        )
      })}
    </div>
  )
}

/** Section 03: whether intent is made out, and what falls with it under s.75(2). */
export function IntentSection({ intent }: { intent: OfficerIntent }) {
  if (!intent.pleaded) return <p className="nrd-read">{intent.line}</p>
  const ifFails = intent.if_fails
  return (
    <>
      {intent.their_words ? (
        <div className="nof-intent-q">
          <p className="ncf-eyebrow">What the notice says shows suppression</p>
          <blockquote className="nof-quote">“{intent.their_words}”</blockquote>
          <p className="nof-src">Their words, as printed. Nira does not paraphrase the department's position.</p>
        </div>
      ) : null}
      <div className="nof-intent">
        <p className="ncf-eyebrow">Is it likely to hold?</p>
        <p className="nrd-read"><b>{intent.word}</b> {intent.runs?.length || intent.sides.length ? null : intent.why} <AgreementMark agreement={intent.agreement} consistent={intent.consistent} /></p>
        {intent.runs?.length ? (
          <div className="nrd-rows nof-runs">
            {intent.runs.map((r, i) => (
              <Code
                key={String(r.made_out)}
                line={<>{intent.runs!.length > 1 ? <b>{i === 0 ? "The stronger reading." : "The other view."}</b> : null} {r.line}</>}
                // The long reasons, and what the act relied on is (or what the notice does not show),
                // are the working: the line itself stays one line.
                working={[r.why, r.made_out === intent.holds && intent.act_relied_on ? `${intent.holds ? "The act relied on" : "What the notice does not show"}: ${intent.act_relied_on}` : ""].filter(Boolean).join(" ")}
                chip={r.made_out ? "made out" : "not made out"}
                tone={r.made_out ? "hurts" : "helps"}
              />
            ))}
          </div>
        ) : intent.sides.length ? (
          <ul className="nof-sides">
            {intent.sides.map((s, i) => (
              <li key={String(s.made_out)}>
                <b>{i === 0 ? "The stronger reading" : "The other view"} finds it {s.made_out ? "made out" : "not made out"}.</b> {s.why}
              </li>
            ))}
          </ul>
        ) : null}
        {!intent.sides.length && !intent.runs?.length && intent.act_relied_on ? <p className="nof-why">{intent.holds ? "The act relied on" : "What the notice does not show"}: {intent.act_relied_on}</p> : null}
      </div>
      <div className="nrd-rows">
        <Code line={intent.if_holds.line} working={withoutCounts(intent.if_holds.working)} chip={`${lakh(intent.if_holds.tax)} tax at stake`} tone="hurts" />
        <Code line={ifFails.line} working={ifFails.working} chip={ifFails.tax ? `${lakh(ifFails.tax)} tax at stake` : "nil in this proceeding"} tone="helps" />
        {ifFails.fresh_line ? (
          <Code
            line={`Fresh s.73 notice after a s.75(2) drop · ${ifFails.fresh_s73.some((f) => f.possible) ? "still possible for some years" : "not possible, out of time"}`}
            working={ifFails.fresh_line}
            chip={ifFails.fresh_s73.some((f) => f.possible) ? "possible" : "not possible"}
            tone={ifFails.fresh_s73.some((f) => f.possible) ? "mixed" : "helps"}
          />
        ) : null}
      </div>
    </>
  )
}

/** Section 06: each finding as the order would put it, your answer beside it. */
export function OrderFindings({ officer, allegations }: { officer: Officer; allegations: Allegation[] }) {
  const titles = new Map(allegations.map((a) => [a.id, a.title]))
  const cards = (officer.allegations ?? []).filter((c) => c.kind === "reading" && c.order_finding)
  if (!cards.length) return <p className="nrd-read">No finding to show: every allegation falls on the time limit or is counted with another.</p>
  return (
    <div className="nof-order">
      <p className="nrd-note">The officer's likely wording, on the notice alone and with no reply filed. A simulated passage, not an order.</p>
      {cards.map((c) => (
        <article className="nof-finding" key={c.id} aria-label={`Finding on ${c.id}`}>
          <p className="ncf-eyebrow">Finding on {c.id} · {titles.get(c.id)} · as the order would put it <AgreementMark agreement={c.call?.agreement} consistent={c.call?.consistent} /></p>
          <blockquote className="nof-quote">“{c.order_finding}”</blockquote>
          {c.best_answer ? (
            <div className="nof-beside">
              <p className="ncf-eyebrow">Your answer, beside it</p>
              <p>{c.best_answer}</p>
            </div>
          ) : null}
        </article>
      ))}
    </div>
  )
}

/** A paid section before the officer's reading has run: its place, said plainly. Dev preview only. */
export function EngineSection({ what, officer }: { what: string; officer?: Officer | null }) {
  return <p className="nrd-read"><EngineSlot what={what} officer={officer} /></p>
}
